import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Restaurant as RestaurantIcon,
  Hotel as HotelIcon,
  Attractions as AttractionsIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DOCUMENT, documentTextButtonSx } from '../constants/documentTheme';
import RoadTripPlanner from './RoadTripPlanner';
import { buildFullRouteStops, renderRoadTripOnMap, scheduleMapLayoutUpdate } from '../utils/renderRoadTripLeafletMap';
import { getRoadTripDayCount, getRoadTripSegmentNumber } from '../utils/roadTripDragUtils';

/**
 * RoadTripMapModal - Shows a road trip route on a Leaflet map.
 * Renders when an itinerary item has road_trip_data in its transportation_data.
 *
 * Props:
 *  - open: boolean
 *  - onClose: function
 *  - roadTripData: { encodedPolyline, orderedStops, totalDistance, totalDuration, googleMapsUrl, dayIndex, totalDays, groupId, allDaysData }
 *  - itemName: string (the itinerary item name that was clicked)
 *  - tripId: number (the trip this road trip belongs to)
 *  - canEdit: boolean (whether the user can edit)
 *  - usePartnerAuth: boolean (a managing partner is editing)
 *  - onEditComplete: () => void (called after a successful edit/save to refresh data)
 */
const RoadTripMapModal = ({
  open,
  onClose,
  roadTripData,
  itemName,
  tripId,
  tripStartDate = null,
  tripEndDate = null,
  canEdit = false,
  usePartnerAuth = false,
  onEditComplete,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitialStep, setEditInitialStep] = useState(0);
  const [dialogReady, setDialogReady] = useState(false);
  const [showFullRoute, setShowFullRoute] = useState(false);

  // Reset dialogReady whenever the main dialog is not visible
  // (either because `open` is false or because `editOpen` hides it)
  useEffect(() => {
    if (!open || editOpen) {
      setDialogReady(false);
    }
  }, [open, editOpen]);

  const handleDialogEntered = () => {
    setDialogReady(true);
  };

  // Build full route stops from allDaysData (flatten, dedupe overnight)
  const fullRouteStops = useMemo(
    () => (showFullRoute ? buildFullRouteStops(roadTripData?.allDaysData) : null),
    [roadTripData?.allDaysData, showFullRoute]
  );

  const hasMultipleDays = (roadTripData?.allDaysData?.length || 0) > 1;

  // Initialize Leaflet map after dialog is fully open and DOM is ready
  useEffect(() => {
    if (!dialogReady || !roadTripData || !mapRef.current || editOpen) return;

    // Destroy previous map if exists
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const allDays = roadTripData.allDaysData || [];
    const useFullRoute = showFullRoute && allDays.length > 1;
    const stops = useFullRoute && fullRouteStops?.length
      ? fullRouteStops
      : roadTripData.orderedStops || [];
    if (stops.length === 0) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    });

    mapInstanceRef.current = map;

    const { bounds } = renderRoadTripOnMap(map, roadTripData, {
      showFullRoute,
      fullRouteStops,
      autoFitBounds: false,
    });

    scheduleMapLayoutUpdate(map, () => {
      if (bounds?.isValid?.()) {
        map.fitBounds(bounds, { padding: [40, 40], animate: false });
      }
    });
  }, [dialogReady, roadTripData, editOpen, showFullRoute, fullRouteStops]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Cleanup when main dialog is not visible (closed or edit mode)
  useEffect(() => {
    if ((!open || editOpen) && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, [open, editOpen]);

  const handleEditClick = () => {
    setEditInitialStep(0);
    setEditOpen(true);
  };

  const handleFindPlacesClick = () => {
    setEditInitialStep(1);
    setEditOpen(true);
  };

  const handleEditSaveComplete = () => {
    setEditOpen(false);
    if (onEditComplete) {
      onEditComplete();
    }
    onClose();
  };

  if (!roadTripData) return null;

  const useFullRoute = hasMultipleDays && showFullRoute;
  const stops = useFullRoute && fullRouteStops?.length
    ? fullRouteStops
    : roadTripData.orderedStops || [];
  const dayLabel = useFullRoute
    ? t('roadTrip.showFullRoute')
    : getRoadTripDayCount(roadTripData) > 1
      ? `${t('roadTrip.day')} ${getRoadTripSegmentNumber(roadTripData)} ${t('roadTrip.of')} ${getRoadTripDayCount(roadTripData)}`
      : null;

  // Extract POIs for sidebar: full route uses flattened stops; single day uses current day
  const currentDayData = (roadTripData.allDaysData || []).find(
    (day, idx) => (day.dayIndex ?? idx) === (roadTripData.dayIndex ?? 0)
  ) || roadTripData.allDaysData?.[0];
  const stopsWithPois = (useFullRoute ? fullRouteStops : (currentDayData?.stops || roadTripData.orderedStops || []))
    ?.map((s, i) => ({
      ...s,
      number: i + 1,
      pois: s.savedPois || [],
    })) || [];

  const getCategoryPoiIcon = (category) => {
    switch (category) {
      case 'restaurant': return <RestaurantIcon sx={{ fontSize: 14 }} />;
      case 'hotel': return <HotelIcon sx={{ fontSize: 14 }} />;
      default: return <AttractionsIcon sx={{ fontSize: 14 }} />;
    }
  };

  return (
    <>
      <Dialog
        open={open && !editOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        disableScrollLock
        TransitionProps={{ onEntered: handleDialogEntered }}
        PaperProps={{
          sx: { borderRadius: '3px', overflow: 'hidden', bgcolor: DOCUMENT.surface, maxWidth: 880 }
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'baseline', gap: 2, pr: 6, flexWrap: 'wrap', borderBottom: `1px solid ${DOCUMENT.rule}`, bgcolor: DOCUMENT.surface }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.4rem' }, color: DOCUMENT.ink }}>
              {itemName || t('roadTrip.routeMap')}
            </Typography>
            {dayLabel && (
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                {dayLabel}
              </Typography>
            )}
          </Box>
          {hasMultipleDays && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button sx={documentTextButtonSx} onClick={() => setShowFullRoute(false)}>
                {t('roadTrip.showDayRoute')}
              </Button>
              <Button sx={documentTextButtonSx} onClick={() => setShowFullRoute(true)}>
                {t('roadTrip.showFullRoute')}
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 3.25, fontSize: 12.5, color: DOCUMENT.ink2, fontVariantNumeric: 'tabular-nums', mr: 4 }}>
            {roadTripData.totalDistance && (
              <Box component="span">
                <Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{roadTripData.totalDistance}</Box>
              </Box>
            )}
            {roadTripData.totalDuration && (
              <Box component="span">
                <Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{roadTripData.totalDuration}</Box>
              </Box>
            )}
            <Box component="span">
              <Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{stops.length}</Box>
              {' '}{t('roadTrip.stops')}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: DOCUMENT.ink2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Map + Legend side-by-side layout */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 500 } }}>
            {/* Map container */}
            <Box
              ref={mapRef}
              sx={{
                flex: { xs: 'none', md: 1 },
                height: { xs: 300, sm: 350, md: '100%' },
                minHeight: 300,
                backgroundColor: '#EDEBE6',
              }}
            />

            {/* Stops & POI Legend Panel */}
            <Box
              sx={{
                width: { xs: '100%', md: 280 },
                minWidth: { md: 280 },
                maxWidth: { md: 320 },
                borderLeft: { md: '1px solid' },
                borderTop: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                overflowY: 'auto',
                maxHeight: { xs: 260, md: '100%' },
                backgroundColor: DOCUMENT.surface,
              }}
            >
              {/* Legend header */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${DOCUMENT.rule}`, backgroundColor: DOCUMENT.surface }}>
                <Typography sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                  {t('roadTrip.stops')} & {t('roadTrip.placesToVisit')}
                </Typography>
                {canEdit && roadTripData.groupId && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                    {t('roadTrip.findPlacesHint')}
                  </Typography>
                )}
              </Box>

              {/* Stops list */}
              <Box sx={{ px: 0 }}>
                {stopsWithPois.map((stop, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === stopsWithPois.length - 1;
                  const markerColor = isFirst
                    ? DOCUMENT.canvas
                    : isLast
                      ? DOCUMENT.oxblood
                      : DOCUMENT.accent;
                  const markerText = isFirst ? DOCUMENT.accent : DOCUMENT.ivory;
                  const markerBorder = isFirst ? `1px solid ${DOCUMENT.accent}` : 'none';

                  return (
                    <Box key={idx}>
                      {/* Stop row */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25 }}>
                        {/* Number badge */}
                        <Box sx={{
                          width: 26, height: 26, borderRadius: '50%',
                          backgroundColor: markerColor, color: markerText,
                          border: markerBorder,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: { xs: '0.8125rem', sm: '0.75rem' }, flexShrink: 0,
                          mt: 0.25,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}>
                          {stop.number}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: { xs: '0.875rem', sm: 'inherit' } }}>
                            {stop.name}
                          </Typography>
                          {stop.address && stop.address !== stop.name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.25, fontSize: { xs: '0.75rem', sm: 'inherit' } }} noWrap>
                              {stop.address}
                            </Typography>
                          )}

                          {/* POIs for this stop */}
                          {stop.pois.length > 0 && (
                            <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {stop.pois.map((poi, pIdx) => (
                                <Box key={pIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 0.5 }}>
                                  <Box sx={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    backgroundColor: DOCUMENT.canvas,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                    color: DOCUMENT.accent,
                                    border: `1px solid ${DOCUMENT.ruleHard}`,
                                  }}>
                                    {getCategoryPoiIcon(poi.category)}
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1.2, display: 'block', fontSize: { xs: '0.75rem', sm: 'inherit' } }} noWrap>
                                      {poi.name}
                                    </Typography>
                                    {poi.rating && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                        <StarIcon sx={{ fontSize: 10, color: '#faaf00' }} />
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.65rem' }, color: 'text.secondary' }}>
                                          {poi.rating}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {!isLast && <Divider sx={{ ml: 5.5 }} />}
                    </Box>
                  );
                })}

                {/* Fallback if no allDaysData — show orderedStops */}
                {stopsWithPois.length === 0 && stops.map((stop, idx) => (
                  <Box key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: '50%',
                        backgroundColor: idx === 0 ? DOCUMENT.canvas
                          : idx === stops.length - 1 ? DOCUMENT.oxblood
                          : DOCUMENT.accent,
                        color: idx === 0 ? DOCUMENT.accent : DOCUMENT.ivory,
                        border: idx === 0 ? `1px solid ${DOCUMENT.accent}` : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: { xs: '0.8125rem', sm: '0.75rem' }, flexShrink: 0,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }}>
                        {idx + 1}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: 'inherit' } }}>
                        {stop.name}
                      </Typography>
                    </Box>
                    {idx < stops.length - 1 && <Divider sx={{ ml: 5.5 }} />}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.75, gap: 3, flexWrap: 'wrap', borderTop: `1px solid ${DOCUMENT.rule}`, justifyContent: 'flex-start' }}>
          <Button onClick={onClose} sx={documentTextButtonSx}>
            {t('common.close')}
          </Button>
          {canEdit && roadTripData.groupId && (
            <>
              <Button onClick={handleFindPlacesClick} sx={documentTextButtonSx}>
                {t('roadTrip.findPlacesToStop')}
              </Button>
              <Button onClick={handleEditClick} sx={documentTextButtonSx}>
                {t('roadTrip.editRoadTrip')}
              </Button>
            </>
          )}
          {(roadTripData.googleMapsUrl || stops.length >= 2) && (
            <Button
              href={(() => {
                const waypoints = [];
                const stopsSource = stopsWithPois.length > 0 ? stopsWithPois : stops.map(s => ({ ...s, pois: [] }));
                stopsSource.forEach(stop => {
                  waypoints.push(`${stop.lat},${stop.lng}`);
                  (stop.pois || []).forEach(poi => {
                    waypoints.push(`${poi.lat},${poi.lng}`);
                  });
                });
                return waypoints.length >= 2
                  ? `https://www.google.com/maps/dir/${waypoints.join('/')}`
                  : roadTripData.googleMapsUrl || '#';
              })()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ml: 'auto',
                background: DOCUMENT.oxblood,
                color: DOCUMENT.ivory,
                borderRadius: '2px',
                fontSize: 12,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                px: 2.5,
                py: 1.25,
                '&:hover': {
                  background: DOCUMENT.nav,
                },
              }}
            >
              {t('roadTrip.openInGoogleMaps')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Road Trip Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="lg"
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: '3px',
            maxHeight: { xs: '95vh', sm: '90vh' },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: DOCUMENT.surface,
          },
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6, flexShrink: 0, py: 1.5 }}>
          <EditIcon sx={{ color: DOCUMENT.accent }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.0625rem', sm: 'inherit' } }}>
            {t('roadTrip.editRoadTrip')}
          </Typography>
          <IconButton
            onClick={() => setEditOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto', p: { xs: 1.5, sm: 2 }, flex: '0 1 auto' }}>
          {editOpen && (
            <RoadTripPlanner
              editMode
              compact
              initialActiveStep={editInitialStep}
              initialDays={roadTripData.allDaysData || [{
                dayIndex: 0,
                stops: (roadTripData.orderedStops || []).map(s => ({
                  name: s.name, lat: s.lat, lng: s.lng, address: s.name, savedPois: []
                }))
              }]}
              editTripId={tripId}
              editGroupId={roadTripData.groupId}
              tripStartDate={tripStartDate}
              tripEndDate={tripEndDate}
              onSaveComplete={handleEditSaveComplete}
              usePartnerAuth={usePartnerAuth}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RoadTripMapModal;
